import {
  MessageBar,
  MessageBarType,
  TooltipHost
} from '@fluentui/react';
import {FC, useCallback, useEffect, useRef, useState} from 'react';

import styles from './MyProcessList.module.scss';
import {useMyProcessList} from './useMyProcessList';
import {HeadingWithDecoration} from '../../common/HeadingWithDecoration';
import {TheButton} from '../../common/TheButton';
import {TreeBuilderFull} from '../../common/TreeBuilder';
import {MiddleTopBarUnit} from '../Unit/MiddleTopBarUnit';
import {scrollIntoElement} from '../../../helpers/visual';
import {Helmet} from 'react-helmet';
import React from 'react';
import {ModalWithDividedRequests} from '../../common/ModalWithDividedRequests';
import { PublishProcessCommentModal } from '../../common/PublishProcessCommentModal/PublishProcessCommentModal';

export const MyProcessList: FC = () => {
  const {
    unitId,
    unitTitle,
    columns,
    itemsFlat,
    revealedMasterProcessIds,
    isAnythingLoading,
    isInvalid,
    errorLabel,
    publishedProcessesForUnit,
    unitPermissions,
    isModalVisible,
    progressStatus,
    onDismiss,
    getBulkModel,
    onCancel,
    onSave,
    onSubmit,
    onSubmitWithComment
  } = useMyProcessList();

  const errorMsgBar = useRef<HTMLDivElement>(null);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState<boolean>(false);
  const [processPublicationComment, setProcessPublicationComment] = React.useState('');
  const onProcessPublicationCommentChange = useCallback(
    (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
      if (!newValue || newValue.length <= 1024) setProcessPublicationComment(newValue || '');
    },
    []
  );
  const onModalWithCommentClose = useCallback(() => setIsCommentModalOpen(false), []);
  const onApproveModal = (sendNotification: boolean) => {
    onSubmitWithComment(processPublicationComment, sendNotification);
    setIsCommentModalOpen(false);
    setProcessPublicationComment('');
  };

  useEffect(() => {
    if (isInvalid) {
      scrollIntoElement(errorMsgBar);
    }
  }, [isInvalid]);

  return (
    <main className={'noPaddingTop'}>
      <MiddleTopBarUnit
        unitId={unitId}
        publishedData={publishedProcessesForUnit?.result}
        unitPermissions={unitPermissions}
        showManage
      />
      {unitTitle && (
        <Helmet>
          <title>IMS {unitTitle} My Processes List</title>
        </Helmet>
      )}
      <HeadingWithDecoration text="My Processes List" />
      {isInvalid && (
        <MessageBar
          className={styles.errorMessage}
          messageBarType={MessageBarType.error}
          ref={errorMsgBar}>
          {errorLabel}
        </MessageBar>
      )}
      <TreeBuilderFull
        bulkHeader="Bulk edit Manage Process Details"
        editColumnPosition={1}
        getBulkModel={getBulkModel}
        columns={columns}
        itemsFlat={itemsFlat}
        turnOffEditNonProcess={true}
        onCancel={onCancel}
        onSubmit={onSave}
        additionalButtons={
          <>
            <TooltipHost content={'Publish minor change. No notification will be sent.'}>
              <TheButton
                primary
                className={styles.split}
                disabled={isAnythingLoading}
                iconProps={{
                  iconName: 'MailPause'
                }}
                onClick={onSubmit}>
                Publish
              </TheButton>
            </TooltipHost>
            <TooltipHost
              content={
                'Publish standard or major change. Next process update will change, notification will be sent and comment can be added.'
              }>
              <TheButton
                primary
                className={styles.split}
                iconProps={{
                  iconName: 'MailAttached'
                }}
                disabled={isAnythingLoading}
                onClick={() => {
                  setIsCommentModalOpen(true);
                }}>
                Publish and confirm process validity
              </TheButton>
            </TooltipHost>
            <PublishProcessCommentModal
              isVisible={isCommentModalOpen}
              dismiss={onModalWithCommentClose}
              processPublicationComment={processPublicationComment}
              onProcessPublicationCommentChange={onProcessPublicationCommentChange}
              onApproveModal={onApproveModal}
            />
          </>
        }
        isLoading={isAnythingLoading}
        initialOpen={revealedMasterProcessIds}
        keepInView
        exportToExcel
        excelFileName="Exported Manage Process Details"
        filterDeleted
      />
      <ModalWithDividedRequests
        header="Information"
        dismiss={onDismiss}
        isVisible={isModalVisible}
        couldBeClosed={false}
        progressStatus={progressStatus}
        body="Due to technological reasons, the query will be sent in packages. Please, be patient."
      />
    </main>
  );
};
